"use client";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "motion/react";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Quote,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  getVisibleTestimonials,
  HOME_TESTIMONIAL_ROTATION_INTERVAL_MS,
} from "@/components/sections/home/testimonialCarousel";

import type { HomeTestimonial } from "@/types/home-messages";

export default function TestimonialsSection() {
  const t = useTranslations("Testimonials");
  const items = (t.raw("items") as HomeTestimonial[]) ?? [];
  const shouldReduceMotion = useReducedMotion();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHoverPaused, setIsHoverPaused] = useState(false);
  const [isFocusPaused, setIsFocusPaused] = useState(false);
  const [isUserPaused, setIsUserPaused] = useState(false);

  const isPaused =
    shouldReduceMotion === true ||
    isHoverPaused ||
    isFocusPaused ||
    isUserPaused;

  useEffect(() => {
    if (items.length <= 1 || isPaused) return;

    const timeout = window.setTimeout(() => {
      setCurrentIndex((previousIndex) =>
        (previousIndex + 1) % items.length,
      );
    }, HOME_TESTIMONIAL_ROTATION_INTERVAL_MS);

    return () => window.clearTimeout(timeout);
  }, [currentIndex, isPaused, items.length]);

  if (items.length === 0) return null;

  const visibleTestimonials = getVisibleTestimonials(items, currentIndex);
  const showPrevious = () => {
    setCurrentIndex((previousIndex) =>
      (previousIndex - 1 + items.length) % items.length,
    );
  };
  const showNext = () => {
    setCurrentIndex((previousIndex) =>
      (previousIndex + 1) % items.length,
    );
  };

  return (
    <section
      aria-labelledby="home-testimonials-title"
      aria-roledescription="carousel"
      className="relative overflow-hidden bg-[#f4f7fb] py-20"
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setIsFocusPaused(false);
        }
      }}
      onFocusCapture={() => setIsFocusPaused(true)}
      onMouseEnter={() => setIsHoverPaused(true)}
      onMouseLeave={() => setIsHoverPaused(false)}
    >
      <div
        aria-hidden="true"
        className="absolute -right-24 top-10 h-64 w-64 rounded-full border border-[#1e3a5f]/10"
      />
      <div
        aria-hidden="true"
        className="absolute -right-10 top-24 h-40 w-40 rounded-full border border-[#f39a3e]/20"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mx-auto mb-12 max-w-3xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#d97720]">
            {t("eyebrow")}
          </p>
          <h2
            className="text-3xl font-bold tracking-tight text-[#142b45] sm:text-4xl"
            id="home-testimonials-title"
          >
            {t("title")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-slate-600">
            {t("subtitle")}
          </p>
        </header>

        <div aria-label={t("carouselLabel")} role="group">
          <div className="grid gap-5 lg:grid-cols-3">
            <AnimatePresence initial={false} mode="popLayout">
              {visibleTestimonials.map((testimonial, slotIndex) => (
                <motion.article
                  animate={{ opacity: 1, y: 0 }}
                  aria-label={t("slideLabel", {
                    current: testimonial.itemIndex + 1,
                    total: items.length,
                  })}
                  aria-roledescription="slide"
                  className={slotIndex === 0 ? "block" : "hidden lg:block"}
                  exit={
                    shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: -12 }
                  }
                  initial={
                    shouldReduceMotion ? false : { opacity: 0, y: 12 }
                  }
                  key={`${testimonial.attribution}-${testimonial.itemIndex}`}
                  layout={!shouldReduceMotion}
                  transition={{ duration: shouldReduceMotion ? 0 : 0.35 }}
                >
                  <Card className="h-full gap-0 border-[#1e3a5f]/10 bg-white p-7 shadow-[0_18px_45px_rgba(20,43,69,0.08)] sm:p-8">
                    <div className="mb-6 flex items-center justify-between">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1e3a5f] text-white">
                        <Quote aria-hidden="true" className="h-5 w-5" />
                      </span>
                      <span
                        aria-hidden="true"
                        className="font-mono text-xs font-semibold tracking-[0.18em] text-slate-400"
                      >
                        {String(testimonial.itemIndex + 1).padStart(2, "0")}
                      </span>
                    </div>

                    <blockquote
                      className="flex-1 text-[1.05rem] leading-8 text-slate-700"
                      lang={testimonial.language}
                    >
                      <span aria-hidden="true">“</span>
                      {testimonial.content}
                      <span aria-hidden="true">”</span>
                    </blockquote>

                    <footer className="mt-8 border-t border-slate-100 pt-5">
                      <div className="mb-3 h-0.5 w-12 bg-gradient-to-r from-[#1e3a5f] to-[#f39a3e]" />
                      <p className="font-semibold text-[#142b45]">
                        {testimonial.attribution}
                      </p>
                    </footer>
                  </Card>
                </motion.article>
              ))}
            </AnimatePresence>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              aria-label={t("previous")}
              className="h-11 w-11 rounded-full border-[#1e3a5f]/20 bg-white text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
              onClick={showPrevious}
              size="icon"
              type="button"
              variant="outline"
            >
              <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            </Button>

            <div
              aria-label={t("positionControls")}
              className="flex items-center"
              role="group"
            >
              {items.map((testimonial, index) => (
                <button
                  aria-label={t("goToSlide", {
                    current: index + 1,
                    total: items.length,
                  })}
                  aria-current={index === currentIndex ? "true" : undefined}
                  className="group flex h-11 w-9 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1e3a5f] focus-visible:ring-offset-2"
                  key={`${testimonial.attribution}-${index}-control`}
                  onClick={() => setCurrentIndex(index)}
                  type="button"
                >
                  <span
                    className={`h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? "w-6 bg-[#f39a3e]"
                        : "w-2 bg-slate-300 group-hover:bg-[#1e3a5f]/50"
                    }`}
                  />
                </button>
              ))}
            </div>

            <Button
              aria-label={t("next")}
              className="h-11 w-11 rounded-full border-[#1e3a5f]/20 bg-white text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
              onClick={showNext}
              size="icon"
              type="button"
              variant="outline"
            >
              <ChevronRight aria-hidden="true" className="h-5 w-5" />
            </Button>

            <Button
              aria-label={isUserPaused ? t("resume") : t("pause")}
              aria-pressed={isUserPaused}
              className="ml-1 h-11 rounded-full border-[#1e3a5f]/20 bg-white px-4 text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
              onClick={() => setIsUserPaused((wasPaused) => !wasPaused)}
              type="button"
              variant="outline"
            >
              {isUserPaused ? (
                <Play aria-hidden="true" className="h-4 w-4" />
              ) : (
                <Pause aria-hidden="true" className="h-4 w-4" />
              )}
              {isUserPaused ? t("resume") : t("pause")}
            </Button>
          </div>

          <p aria-live="polite" className="sr-only">
            {t("position", {
              current: currentIndex + 1,
              total: items.length,
            })}
          </p>
        </div>
      </div>
    </section>
  );
}
